// src/firebase.ts
import { initializeApp, getApps } from "firebase/app";
// ÄNDRING HÄR: Importerar ReCaptchaEnterpriseProvider istället för V3
import { initializeAppCheck, ReCaptchaEnterpriseProvider, CustomProvider } from "firebase/app-check";
import { initializeFirestore, setLogLevel } from "firebase/firestore";
import type { Firestore } from "firebase/firestore";

import { getStorage } from "firebase/storage";
import type { FirebaseStorage } from "firebase/storage";

import { getAuth } from "firebase/auth";
import type { ActionCodeSettings } from "firebase/auth";

// ---- Typer för runtime-config (från /config.js) ----
type RuntimeFirebaseConfig = {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
};

declare global {
  interface Window {
    __GOLDWASSER_CONFIG__?: { firebase?: RuntimeFirebaseConfig };
  }
}

// ---- Hämta runtime-config om den finns ----
const runtimeCfg: RuntimeFirebaseConfig =
  (typeof window !== "undefined" &&
    window.__GOLDWASSER_CONFIG__ &&
    window.__GOLDWASSER_CONFIG__.firebase) ||
  {};

// ---- Miljövariabler (CRA) med rimliga fallback-värden ----
const envCfg = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// ---- Slutlig config: env som bas, runtime får prioritet (om värdet finns) ----
const validRuntimeCfg = Object.entries(runtimeCfg).reduce((acc, [key, value]) => {
  if (value) {
    (acc as any)[key] = value;
  }
  return acc;
}, {} as RuntimeFirebaseConfig);
const firebaseConfig = { ...envCfg, ...validRuntimeCfg };

// ---- Initiera appen endast en gång ----
const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

// ===== Email verification settings =====
const RAW_ORIGIN =
  (typeof window !== "undefined" && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname))
    ? window.location.origin
    : (process.env.REACT_APP_PUBLIC_APP_ORIGIN || "https://din-produktionsdomän.se");

// Trimma ev. trailing slash från env-värdet
const APP_ORIGIN = String(RAW_ORIGIN).replace(/\/+$/, "");

export const EMAIL_VERIFICATION_ACS: ActionCodeSettings = {
  url: `${APP_ORIGIN}/verify`,
  handleCodeInApp: true,
};

// En explicit buildvariabel (även en tom sådan) måste vinna över runtime-fallbacken.
// Det hindrar att en stagingbuild råkar använda produktionsnyckeln i public/config.js.
const envAppCheckSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const appCheckSiteKey = envAppCheckSiteKey !== undefined
  ? envAppCheckSiteKey
  : ((typeof window !== "undefined" && (window as any).__GOLDWASSER_CONFIG__?.appCheck?.siteKey) || "");


// Aktivera App Check (robust metod)
if (typeof window !== "undefined") {
  try {
    if (process.env.NODE_ENV === "production") {
      if (!appCheckSiteKey) {
        console.warn("[AppCheck] Skipped: this environment has no configured site key.");
      } else {
      // I produktion: använd reCAPTCHA Enterprise.
      // ÄNDRING HÄR: Använder ReCaptchaEnterpriseProvider
        initializeAppCheck(app, {
          provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
          isTokenAutoRefreshEnabled: true,
        });
        console.log("[AppCheck] Initialized in PROD mode with reCAPTCHA Enterprise.");
      }
    } else {
      // I utvecklingsläge: använd ENDAST debug-token.
      (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;
      
      const debugProvider = new CustomProvider({
        getToken: () =>
          Promise.resolve({ token: "dummy-token", expireTimeMillis: Date.now() + 60 * 60 * 1000 }),
      });

      initializeAppCheck(app, { provider: debugProvider, isTokenAutoRefreshEnabled: true });
      console.log("[AppCheck] Initialized in DEV mode (debug token).");
    }
  } catch (err) {
    console.warn("[AppCheck] init failed:", err);
  }
}


// ---- Init Auth, Firestore och Storage ----
export const auth = getAuth(app);

// Sätt persistence state till 'session' för delade arbetsstationer (säkerhetsåtgärd)
try {
  const { setPersistence, browserSessionPersistence } = await import("firebase/auth");
  setPersistence(auth, browserSessionPersistence);
  console.log("[Auth] Persistence state set to 'session'.");
} catch (err) {
  console.error("[Auth] Failed to set persistence state:", err);
}

// Stabilize Firestore networking in dev/proxy environments
const IS_LOCALHOST = typeof window !== "undefined" && typeof location !== "undefined" && location.hostname === "localhost";
const firestoreSettings: any = IS_LOCALHOST
  ? { experimentalForceLongPolling: true }
  : { experimentalAutoDetectLongPolling: true };
export const db: Firestore = initializeFirestore(app, firestoreSettings);
export const storage: FirebaseStorage = getStorage(app);


// --- Debug: visa endast om ?debug=1 OCH appen explicit tillåter (admin) ---
declare global { interface Window { __GW_DEBUG__?: any; __GW_DEBUG_ALLOWED__?: boolean } }

try {
  const DEBUG =
    typeof window !== "undefined" &&
    new URLSearchParams(location.search).get("debug") === "1";

  const logDebug = () => {
    const safeOptions = { ...app.options, apiKey: "★redacted★" };
    (window as any).__GW_DEBUG__ = {
      firebaseOptions: safeOptions,
      projectId: app.options.projectId,
      storageBucket: app.options.storageBucket,
      authDomain: app.options.authDomain,
    };
    console.log("[env] firebase options", safeOptions);
    console.log("[build] commit", process.env.REACT_APP_COMMIT_SHA || "unknown");
    console.log("[build] time  ", process.env.REACT_APP_BUILD_TIME || "unknown");
  };

  if (DEBUG && (window as any).__GW_DEBUG_ALLOWED__ === true) {
    logDebug();
  }

  if (DEBUG) {
    try { setLogLevel("debug"); } catch {/*avsiktligt*/ }
    document.addEventListener("gw:debug-ready", () => {
      if ((window as any).__GW_DEBUG_ALLOWED__ === true) logDebug();
    }, { once: true });
  }
} catch {
  // Ignorera fel i SSR/icke-browser miljöer
}

// Gör auth tillgänglig i DevTools i dev-läge
if (process.env.NODE_ENV !== "production") {
  (window as any).auth = auth;
}
